import { useState } from "react";
import { Link } from "react-router-dom";
import PublicNav from "@/components/PublicNav";
import Footer from "@/components/Footer";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { Phone, Mail, MessageSquare, MapPin, Loader2, CheckCircle2, ShieldCheck } from "lucide-react";

const MSG_LIMIT = 160;

export default function Contact() {
  const [form, setForm] = useState({ name: "", email: "", phone: "", message: "" });
  const [saving, setSaving] = useState(false);
  const [done, setDone] = useState(false);

  const submit = async () => {
    if (!form.name || !form.email || !form.message) { toast.error("Please fill name, email and message"); return; }
    setSaving(true);
    try {
      await api.post("/contact", form);
      setDone(true);
      toast.success("Message sent! We'll get back to you soon.");
    } catch {
      toast.error("Failed to send. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="min-h-screen bg-white">
      <PublicNav />
      <main className="max-w-6xl mx-auto px-6 py-16 lg:py-20 grid grid-cols-1 lg:grid-cols-2 gap-12">
        <div>
          <p className="text-sm font-semibold uppercase tracking-wider text-[#0E9AC8]">Contact us</p>
          <h1 className="mt-3 font-display text-4xl lg:text-5xl tracking-tighter font-extrabold text-slate-900">Let's talk about your build.</h1>
          <p className="mt-5 text-slate-600 leading-relaxed max-w-md">
            Have a question about your project or how Nirmantra.ai works? Send us a message and our team will respond within one business day.
          </p>
          <div className="mt-10 space-y-5">
            <a href="tel:+919318344231" data-testid="contact-phone-link" className="flex items-center gap-4 group">
              <div className="h-11 w-11 rounded-xl bg-[#0E9AC8]/10 flex items-center justify-center">
                <Phone className="h-5 w-5 text-[#0E9AC8]" strokeWidth={1.6} />
              </div>
              <div>
                <p className="text-sm font-semibold text-slate-900 group-hover:text-[#0E9AC8] transition-colors">Phone</p>
                <p className="text-sm text-slate-500">+91 93183 44231</p>
              </div>
            </a>
            <a href="mailto:info@nirmantra.ai" data-testid="contact-email-link" className="flex items-center gap-4 group">
              <div className="h-11 w-11 rounded-xl bg-[#0E9AC8]/10 flex items-center justify-center">
                <Mail className="h-5 w-5 text-[#0E9AC8]" strokeWidth={1.6} />
              </div>
              <div>
                <p className="text-sm font-semibold text-slate-900 group-hover:text-[#0E9AC8] transition-colors">Email</p>
                <p className="text-sm text-slate-500">info@nirmantra.ai</p>
              </div>
            </a>
            {[[MessageSquare, "Advisory", "Independent experts, no sales pressure"], [MapPin, "Serving", "Homeowners & businesses across India"]].map(([Icon, t, d], i) => (
              <div key={i} className="flex items-center gap-4">
                <div className="h-11 w-11 rounded-xl bg-[#0E9AC8]/10 flex items-center justify-center">
                  <Icon className="h-5 w-5 text-[#0E9AC8]" strokeWidth={1.6} />
                </div>
                <div>
                  <p className="text-sm font-semibold text-slate-900">{t}</p>
                  <p className="text-sm text-slate-500">{d}</p>
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="card-premium rounded-2xl p-8">
          {done ? (
            <div className="h-full flex flex-col items-center justify-center text-center py-10" data-testid="contact-success">
              <CheckCircle2 className="h-12 w-12 text-[#0E9AC8] mb-4" strokeWidth={1.5} />
              <h3 className="font-display text-2xl font-bold text-slate-900">Thank you!</h3>
              <p className="mt-2 text-slate-500">Your message has been received. We'll be in touch shortly.</p>
            </div>
          ) : (
            <div className="space-y-5">
              <h3 className="font-display text-2xl font-bold text-slate-900">Send a message</h3>
              <div>
                <Label>Full name *</Label>
                <Input data-testid="contact-name" className="rounded-xl mt-1.5" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
              </div>
              <div>
                <Label>Email *</Label>
                <Input data-testid="contact-email" type="email" className="rounded-xl mt-1.5" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} />
              </div>
              <div>
                <Label>Contact number</Label>
                <Input data-testid="contact-phone" type="tel" className="rounded-xl mt-1.5" placeholder="+91 98765 43210" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} />
              </div>
              <div>
                <div className="flex items-center justify-between">
                  <Label>Message *</Label>
                  <span className={`text-xs ${form.message.length >= MSG_LIMIT ? "text-[#0E9AC8]" : "text-slate-400"}`} data-testid="contact-message-counter">
                    {form.message.length}/{MSG_LIMIT}
                  </span>
                </div>
                <Textarea data-testid="contact-message" maxLength={MSG_LIMIT} className="rounded-xl mt-1.5 min-h-[110px]"
                  placeholder="Write anything (up to 160 characters)…"
                  value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value.slice(0, MSG_LIMIT) })} />
              </div>
              <Button data-testid="contact-submit" onClick={submit} disabled={saving}
                className="w-full bg-[#0E9AC8] hover:bg-[#0B7EA4] text-white rounded-xl h-11 transition-colors">
                {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Send message"}
              </Button>

              {/* Privacy disclosure */}
              <div className="flex items-start gap-2 pt-1">
                <ShieldCheck className="h-4 w-4 text-slate-400 mt-0.5 shrink-0" strokeWidth={1.6} />
                <p className="text-xs text-slate-400 leading-relaxed">
                  By submitting this form you agree to our{" "}
                  <Link to="/privacy" className="text-slate-500 underline hover:text-[#0E9AC8]">Privacy Policy</Link> and{" "}
                  <Link to="/terms" className="text-slate-500 underline hover:text-[#0E9AC8]">Terms &amp; Conditions</Link>.
                  Your information is kept confidential and never shared with third parties.
                </p>
              </div>
            </div>
          )}
        </div>
      </main>

      {/* Copyright + privacy disclosures */}
      <div className="max-w-6xl mx-auto px-6 pb-10">
        <div className="border-t border-slate-100 pt-6 flex flex-col sm:flex-row items-center justify-between gap-3">
          <p className="text-xs text-slate-400">© 2026 Nirmantra.ai. All rights reserved. Independent advisory — not a contractor or builder.</p>
          <div className="flex items-center gap-4 text-xs">
            <Link to="/privacy" className="text-slate-400 hover:text-[#0E9AC8] transition-colors">Privacy Policy</Link>
            <Link to="/terms" className="text-slate-400 hover:text-[#0E9AC8] transition-colors">Terms &amp; Conditions</Link>
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
}
